import { DEFAULTS } from 'components/Dashboard/constants';
import Styled from 'components/Dashboard/styles';
import useToast from 'hooks/useToast';
import Loading from 'layouts/Loading/loading';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import UserAPI from 'services/apis/UserAPI';
import UserActions from 'store/stores/User/actions';

const PasswordModule = ({ userData, onUpdate }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('dashboard');
  const [password, setPassword] = useState({ current: '', new: '' });
  const [isPassword, setIsPassword] = useState({
    current: false,
    new: false
  });
  const [passwordStrength, setPasswordStrength] = useState(DEFAULTS.password);
  const [isLoading, setIsLoading] = useState(false);
  const localUserAPI = new UserAPI();
  const toast = useToast();

  useEffect(() => {
    setPassword({ current: '', new: '' });
    setPasswordStrength(DEFAULTS.password);
  }, [userData]);

  const onHandleChange = event => {
    if (!event.target.disabled) {
      const _name = event.target.name;
      const _value = event.target.value;

      if (_name === 'new') {
        setPasswordStrength({
          lowerCase: /[a-z]+/.test(_value),
          upperCase: /[A-Z]+/.test(_value),
          number: /[\d]+/.test(_value),
          charLength: _value.length > 7
        });
      }

      setPassword({ ...password, [_name]: _value });
    }
  };

  const onCheckPassword = () =>
    Object.values(passwordStrength).reduce((a, item) => a + item, 0);

  const onSavePassword = () => {
    if (onCheckPassword() > 3 && password.current.length > 0) {
      setIsLoading(true);
      const _body = {
        current: password.current,
        new: password.new
      };

      localUserAPI.changePassword(_body).then(response => {
        if (response && response.id) {
          if (!!onUpdate) onUpdate();
          setTimeout(() => {
            toast(t('notification.password.success'), 'success');
            setIsLoading(false);
            setTimeout(() => {
              dispatch(UserActions.SetIsLogin(false));
            }, 1000);
          }, 1000);
        } else {
          setTimeout(() => {
            toast(response.msg || t('notification.password.error'), 'error');
            setIsLoading(false);
          }, 1000);
        }
      });
    }
  };

  return (
    <Styled.Content>
      <Styled.Title>{t('form.title.password')}</Styled.Title>
      <Styled.Spacer size={15} />
      <Styled.Fields>
        <Styled.Field>
          <input
            id={'current'}
            name={'current'}
            type={!isPassword.current ? 'password' : 'text'}
            className={'input'}
            placeholder={t('form.label.current_password')}
            value={password.current}
            disabled={false}
            autoComplete={'new-password'}
            onChange={onHandleChange}
          />
          <svg
            className={`icon ${isPassword.current ? 'on' : ''}`}
            viewBox="0 0 31 23"
            onClick={() =>
              setIsPassword({
                ...isPassword,
                current: !isPassword.current
              })
            }
          >
            <path d="M4.82199 15.828C5.4083 16.2372 5.99958 16.6139 6.59346 16.9583L0.384331 21.5616L1.47134 23L8.36895 17.8865C10.7681 19.0047 13.1848 19.5857 15.5 19.5857C18.9651 19.5857 22.6575 18.2862 26.1782 15.828C28.8116 13.9892 30.4184 12.1651 30.4856 12.0883L31 11.5L30.4856 10.9117C30.4184 10.8351 28.8116 9.01108 26.1782 7.17227C25.5806 6.7548 24.978 6.37176 24.3725 6.02199L30.5551 1.43844L29.4681 0L22.5939 5.09638C20.207 3.9892 17.8034 3.41453 15.5 3.41453C12.0349 3.41453 8.34246 4.71381 4.82199 7.17227C2.18844 9.01108 0.581581 10.8351 0.514412 10.9117L0 11.5L0.514412 12.0883C0.581581 12.1649 2.18844 13.9892 4.82199 15.828ZM9.15109 11.5C9.15109 8.03252 11.9992 5.21117 15.5 5.21117C15.5598 5.21117 15.6194 5.21305 15.679 5.21398C17.3507 5.2599 18.863 5.94937 19.9717 7.04038L18.4975 8.13326C17.6977 7.43372 16.6485 7.00805 15.5 7.00805C12.9996 7.00805 10.9651 9.02303 10.9651 11.5C10.9651 12.1792 11.1189 12.8229 11.3925 13.4007L9.91786 14.4938C9.42899 13.6031 9.15109 12.583 9.15109 11.5ZM18.2211 11.5C18.2211 12.9862 17.0004 14.1953 15.5 14.1953C14.9352 14.1953 14.4099 14.0236 13.9745 13.7307L18.0912 10.6786C18.1752 10.9377 18.2211 11.2137 18.2211 11.5ZM12.7792 11.5C12.7792 10.014 13.9998 8.80492 15.5 8.80492C16.0511 8.80492 16.5641 8.96844 16.9931 9.24864L12.8972 12.285C12.8206 12.0365 12.7792 11.7729 12.7792 11.5ZM12.4745 14.8426C13.2777 15.5567 14.3383 15.992 15.5 15.992C18.0006 15.992 20.0349 13.977 20.0349 11.5C20.0349 10.808 19.8759 10.152 19.5924 9.56584L21.0644 8.4746C21.5641 9.3728 21.8489 10.4038 21.8489 11.5C21.8489 14.9677 19.0008 17.7888 15.5 17.7888C13.7498 17.7888 12.1564 17.0825 11.0023 15.9341L12.4745 14.8426ZM25.0955 8.61446C26.6875 9.72187 27.8937 10.8412 28.5523 11.4998C27.5434 12.5067 25.245 14.5962 22.2157 16.0903C23.1277 14.7854 23.6629 13.2036 23.6629 11.5C23.6629 10.0004 23.2481 8.59525 22.527 7.39015L22.7829 7.20062C23.6497 7.6605 24.4285 8.1506 25.0955 8.61446ZM8.78426 6.90965C7.87228 8.21455 7.33705 9.79637 7.33705 11.5C7.33705 12.9867 7.7448 14.3806 8.45433 15.5787L8.18187 15.7806C7.32925 15.3264 6.56248 14.8431 5.9045 14.3855C4.31255 13.2781 3.1061 12.1585 2.44742 11.5002C3.45661 10.4933 5.75526 8.40385 8.78426 6.90965Z" />
          </svg>
        </Styled.Field>

        <Styled.Field>
          <input
            id={'new'}
            name={'new'}
            type={!isPassword.new ? 'password' : 'text'}
            className={'input'}
            placeholder={t('form.label.new_password')}
            value={password.new}
            disabled={false}
            autoComplete={'new-password'}
            onChange={onHandleChange}
          />
          <svg
            className={`icon ${isPassword.new ? 'on' : ''}`}
            viewBox="0 0 31 23"
            onClick={() =>
              setIsPassword({
                ...isPassword,
                new: !isPassword.new
              })
            }
          >
            <path d="M4.82199 15.828C5.4083 16.2372 5.99958 16.6139 6.59346 16.9583L0.384331 21.5616L1.47134 23L8.36895 17.8865C10.7681 19.0047 13.1848 19.5857 15.5 19.5857C18.9651 19.5857 22.6575 18.2862 26.1782 15.828C28.8116 13.9892 30.4184 12.1651 30.4856 12.0883L31 11.5L30.4856 10.9117C30.4184 10.8351 28.8116 9.01108 26.1782 7.17227C25.5806 6.7548 24.978 6.37176 24.3725 6.02199L30.5551 1.43844L29.4681 0L22.5939 5.09638C20.207 3.9892 17.8034 3.41453 15.5 3.41453C12.0349 3.41453 8.34246 4.71381 4.82199 7.17227C2.18844 9.01108 0.581581 10.8351 0.514412 10.9117L0 11.5L0.514412 12.0883C0.581581 12.1649 2.18844 13.9892 4.82199 15.828ZM9.15109 11.5C9.15109 8.03252 11.9992 5.21117 15.5 5.21117C15.5598 5.21117 15.6194 5.21305 15.679 5.21398C17.3507 5.2599 18.863 5.94937 19.9717 7.04038L18.4975 8.13326C17.6977 7.43372 16.6485 7.00805 15.5 7.00805C12.9996 7.00805 10.9651 9.02303 10.9651 11.5C10.9651 12.1792 11.1189 12.8229 11.3925 13.4007L9.91786 14.4938C9.42899 13.6031 9.15109 12.583 9.15109 11.5ZM18.2211 11.5C18.2211 12.9862 17.0004 14.1953 15.5 14.1953C14.9352 14.1953 14.4099 14.0236 13.9745 13.7307L18.0912 10.6786C18.1752 10.9377 18.2211 11.2137 18.2211 11.5ZM12.7792 11.5C12.7792 10.014 13.9998 8.80492 15.5 8.80492C16.0511 8.80492 16.5641 8.96844 16.9931 9.24864L12.8972 12.285C12.8206 12.0365 12.7792 11.7729 12.7792 11.5ZM12.4745 14.8426C13.2777 15.5567 14.3383 15.992 15.5 15.992C18.0006 15.992 20.0349 13.977 20.0349 11.5C20.0349 10.808 19.8759 10.152 19.5924 9.56584L21.0644 8.4746C21.5641 9.3728 21.8489 10.4038 21.8489 11.5C21.8489 14.9677 19.0008 17.7888 15.5 17.7888C13.7498 17.7888 12.1564 17.0825 11.0023 15.9341L12.4745 14.8426ZM25.0955 8.61446C26.6875 9.72187 27.8937 10.8412 28.5523 11.4998C27.5434 12.5067 25.245 14.5962 22.2157 16.0903C23.1277 14.7854 23.6629 13.2036 23.6629 11.5C23.6629 10.0004 23.2481 8.59525 22.527 7.39015L22.7829 7.20062C23.6497 7.6605 24.4285 8.1506 25.0955 8.61446ZM8.78426 6.90965C7.87228 8.21455 7.33705 9.79637 7.33705 11.5C7.33705 12.9867 7.7448 14.3806 8.45433 15.5787L8.18187 15.7806C7.32925 15.3264 6.56248 14.8431 5.9045 14.3855C4.31255 13.2781 3.1061 12.1585 2.44742 11.5002C3.45661 10.4933 5.75526 8.40385 8.78426 6.90965Z" />
          </svg>
        </Styled.Field>
        <Styled.Field field={'password-strength'}>
          <div className={'strength'}>
            <span
              className={`${
                onCheckPassword() >= 1
                  ? onCheckPassword() > 3
                    ? 'ok'
                    : 'on'
                  : ''
              }`}
            />
            <span
              className={`${
                onCheckPassword() >= 2
                  ? onCheckPassword() > 3
                    ? 'ok'
                    : 'on'
                  : ''
              }`}
            />
            <span
              className={`${
                onCheckPassword() >= 3
                  ? onCheckPassword() > 3
                    ? 'ok'
                    : 'on'
                  : ''
              }`}
            />
            <div
              className={`text ${
                onCheckPassword() > 1
                  ? onCheckPassword() > 3
                    ? 'ok'
                    : 'on'
                  : ''
              }`}
            >
              {onCheckPassword() === 0
                ? 'خالی'
                : onCheckPassword() === 1
                ? 'بسیار ضعیف'
                : onCheckPassword() === 2
                ? 'ضعیف'
                : onCheckPassword() === 3
                ? 'قوی'
                : 'بسیار قوی'}
            </div>
          </div>
        </Styled.Field>
        <Styled.Action size={'full'}>
          <Styled.SaveButton
            isDisabled={onCheckPassword() < 4 || password.current.length === 0}
            onClick={onSavePassword}
          >
            {isLoading ? <Loading color={'#fff'} /> : t('action.save_password')}
          </Styled.SaveButton>
        </Styled.Action>
      </Styled.Fields>
    </Styled.Content>
  );
};

export default PasswordModule;
